// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@bg/App/Components/Notify";
import loadSystemList from "@bg/App/Handlers/CommonHandlers/LoadSystemList";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { addToListModalStyles, GroupItem, GroupList } from "@bg/Overrides/Components/AddToListModal";
import { myListsDetailsOptionsStyles } from "@bg/Overrides/Widgets/MyListsDetails/MyListDetailsOptionsStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/UpdateWishList";
import addWishList from "@insite/client-framework/Store/Pages/MyLists/Handlers/AddWishList";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const groupListName = state.context.website.properties["groupsListName"];
    return {
        listGroups: (state.xSystemList as any)[groupListName]?.values,
        uncategorizedGroupName: (getSettingsCollection(state).customSettings as any).unCategorizedGroupName as string,
        groupsNamingRegex: (getSettingsCollection(state).customSettings as any).groupsNamingRegex as string,
        groupListName,
    };
};

interface OwnProps {
    wishList?: WishListModel;
    onCancel: () => void;
    onSubmit: () => void;
}

interface State {
    name: string;
    nameError?: React.ReactNode;
    description: string;
    descriptionError?: React.ReactNode;
    groupName?: string;
    newGroupError?: string;
    selectedGroup?: string;
    currentStep: string;
}

const mapDispatchToProps = {
    addWishList,
    updateWishList,
    loadSystemList,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsEditListFormStyles {
    container?: InjectableCss;
    nameTextField?: TextFieldProps;
    descriptionTextField?: TextFieldProps;
    cancelButton?: ButtonPresentationProps;
    createButton?: ButtonPresentationProps;
}

export const myListsEditListFormStyles: MyListsEditListFormStyles = {
    container: {
        css: css`
            position: relative;
        `,
    },
    nameTextField: {
        cssOverrides: {
            formField: css`
                max-width: 540px;
                margin-bottom: 20px;
                label {
                    font-weight: 500;
                }
            `,
        },
    },
};

/**
 * @deprecated Use myListsEditListFormStyles instead.
 */
export const createListFormStyles = myListsEditListFormStyles;
export const styles = { ...myListsEditListFormStyles, ...addToListModalStyles, ...myListsDetailsOptionsStyles };

const MyListsEditListForm: React.FC<Props> = ({
    wishList,
    addWishList,
    loadSystemList,
    updateWishList,
    listGroups,
    onCancel,
    onSubmit,
    uncategorizedGroupName,
    groupListName,
    groupsNamingRegex,
}) => {
    const toasterContext = useContext(ToasterContext);
    const [name, setName] = useState(wishList?.name || "");
    const [nameError, setNameError] = useState("");
    const [description, setDescription] = useState(wishList?.description || "");
    const [descriptionError, setDescriptionError] = useState("");
    const [selectedGroup, setSelectedGroup] = useState("");
    const [currentStep, setCurrentStep] = useState("");
    const [groupName, setGroupName] = useState("");
    const [newGroupError, setNewGroupError] = useState("");
    const [inProgress, setInProgress] = useState(false);
    const nameRegex = new RegExp(groupsNamingRegex);

    useEffect(() => {
        if (!listGroups || !listGroups.length) {
            loadSystemList({
                systemListName: groupListName,
            });
        }
    }, []);

    const submitHandler = (e?: any, groupName?: string) => {
        if (name === "") {
            const error = siteMessage("Lists_List_Name_Required") as string;
            setNameError(error);
            return;
        }

        const groups = JSON.parse(
            wishList ? wishList?.properties.groups : `["${uncategorizedGroupName.toLowerCase()}"]`,
        );
        selectedGroup || groupName ? groups.push(selectedGroup || groupName) : null;
        if (wishList) {
            setInProgress(true);
            updateWishList({
                apiParameter: {
                    wishList: {
                        ...wishList,
                        name,
                        description,
                        properties: {
                            ...wishList.properties,
                            groups: groups.join(),
                        },
                    },
                },
                onSuccess: onSubmitSuccess,
                onError: onSubmitError,
                onComplete(resultProps) {
                    setInProgress(false);
                    if (resultProps.result?.wishList) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    } else if (resultProps.result?.errorMessage) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onError?.(resultProps.result.errorMessage);
                    }
                },
            });
        } else {
            setInProgress(true);
            addWishList({
                apiParameter: {
                    name,
                    description,
                    groups,
                } as any,
                onSuccess: onSubmitSuccess,
                onError: onSubmitError,
                onComplete(resultProps) {
                    setInProgress(false);
                    if (resultProps.result?.wishListModel) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result?.wishListModel);
                    } else if (resultProps.result?.errorMessage) {
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onError?.(resultProps.result.errorMessage);
                    }
                },
            });
        }
    };

    const onSubmitSuccess = () => {
        toasterContext.addToast({
            body: translate(`List ${wishList ? "Updated" : "Created"}`),
            messageType: "success",
        });
        onSubmit();
    };

    const onSubmitError = (errorMessage: string) => {
        setInProgress(false);
        setNameError(errorMessage);
    };

    const nameChangeHandler = (e: any) => {
        setName(e.target.value);
        setNameError(e.target.value.length > 100 ? (siteMessage("Lists_List_Name_Too_Long", "100") as string) : "");
    };

    const descriptionChangeHandler = (e: any) => {
        setDescription(e.target.value);
        setDescriptionError(
            e.target.value.length > 300 ? (siteMessage("Lists_List_Description_Too_Long", "300") as string) : "",
        );
    };

    const handleGroupSelection = (groupName: string) => {
        if (selectedGroup.toLowerCase() === groupName.toLowerCase()) {
            setSelectedGroup("");
        } else {
            setSelectedGroup(groupName);
        }
    };

    const newGroupInputChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setGroupName(event.target.value);
        if (event.target.value) {
            setNewGroupError("");
        }
    };

    const addNewGroupAndGoBack = () => {
        if (!groupName || !groupName.length) {
            setNewGroupError("Group Name Requried");
            return;
        }
        if (!nameRegex.test(groupName)) {
            setNewGroupError(siteMessage("Custom_Group_Name_Error_Text") as string);
            return;
        }
        submitHandler({}, groupName);
    };

    const handleReturnFromNewGroup = () => {
        setGroupName("");
        setCurrentStep("");
    };

    const validateProjectListNameBeforeGoingToNewBlankGroup = () => {
        if (!name) {
            setNameError(siteMessage("Lists_List_Name_Required") as string);
            return;
        }
        setCurrentStep("newGroup");
    };

    return (
        <LoadingOverlay loading={inProgress}>
            <StyledWrapper {...styles.container} data-test-selector="editListForm">
                <StyledWrapper {...styles.stepWrapper} className={currentStep === "" ? "in-view" : ""}>
                    <TextField
                        data-test-selector="myListsEditListFormName"
                        label={translate("List Name")}
                        {...styles.nameTextField}
                        name="name"
                        error={nameError}
                        maxLength={50}
                        required
                        defaultValue={name}
                        onChange={nameChangeHandler}
                    />

                    <TextField
                        data-test-selector="myListsEditListFormDescription"
                        label={translate("Description")}
                        {...styles.newListInput}
                        name="description"
                        error={descriptionError}
                        defaultValue={description}
                        onChange={descriptionChangeHandler}
                    />

                    {listGroups && listGroups.length > 0 && !wishList && (
                        <StyledWrapper {...styles.groupsWrapper}>
                            {listGroups.map((group: GroupList) => (
                                <Clickable
                                    className={
                                        selectedGroup.toLowerCase() === group.name.toLowerCase() ? "selected" : ""
                                    }
                                    key={group.id}
                                    {...styles.groupItem}
                                    onClick={() => handleGroupSelection(group.name)}
                                >
                                    <GroupItem imagePath={group.additionalInfo} groupName={group.description} />
                                </Clickable>
                            ))}
                            <StyledWrapper {...styles.groupItem}>
                                <StyledWrapper {...styles.addNewGroupItem}>
                                    <Button
                                        onClick={validateProjectListNameBeforeGoingToNewBlankGroup}
                                        {...styles.addNewButton}
                                    >
                                        <ButtonIcon src="Plus" size={16} />
                                    </Button>
                                </StyledWrapper>
                                <Typography lineHeight="16px" transform="uppercase" color="secondary">
                                    {translate("New Blank Group")}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    )}
                    <StyledWrapper className="modal-actions">
                        <Button
                            {...styles.addButton}
                            onClick={submitHandler}
                            disabled={name.length > 100 || description.length > 300}
                            data-test-selector="myListsEditListFormSubmit"
                        >
                            {translate(wishList ? "Save" : "Create List")}
                        </Button>
                        <Button {...styles.cancelButton} onClick={onCancel}>
                            {translate("Cancel")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>

                {/* new group name */}
                <StyledWrapper {...styles.stepWrapper} className={currentStep === "newGroup" ? "in-view" : ""}>
                    <TextField
                        label={translate("Group Name")}
                        value={groupName}
                        error={newGroupError}
                        maxLength={50}
                        onChange={newGroupInputChangeHandler}
                        placeholder={translate("New Group Name")}
                        {...styles.newListInput}
                        data-test-selector="productAddToListModal_newGroupName"
                    />
                    {groupsNamingRegex && (
                        <StyledWrapper {...styles.notifyStyle}>
                            <Notify messageType="info" body={siteMessage("Custom_GroupName_Limitations_Text")} />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.buttonsWrapper} className="modal-actions">
                        <Button
                            {...styles.addButton}
                            onClick={addNewGroupAndGoBack}
                            data-test-selector="productAddToListModal_addButton"
                            disabled={inProgress}
                        >
                            {translate("Save and Continue")}
                        </Button>
                        <Button {...styles.cancelButton} onClick={handleReturnFromNewGroup}>
                            {translate("Return to Previous Screen")}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </LoadingOverlay>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyListsEditListForm);
